export const MemberPermissions = {
    ReadMember: 'members:read',
    CreateMember: 'members:create',
    UpdateMember: 'members:update',
    ReadAllMembers: 'members:read-all',
    SubmitNewMemberForm: 'members:newmember',
    CanSignUpGuest: 'members:createguest',
    RegisterAny: 'members:registerany',
    TourRegistration: 'members:tour-registration'
};

export const BookingPermissions = {
    Read: 'bookings:read',
    Create: 'bookings:create',
    Edit: 'bookings:edit',
    Cancel: 'bookings:cancel'
};

export const AdminPermissions = {
    Access: 'admin:access',
    FullAccess: 'admin:full',
    FortiusAdmin: 'admin:fortius',
    ClubAdmin: 'admin:club',
    // Club owner / GM tier. Gates the club's own management screens (CRM > Shift
    // Checklists) — distinct from ClubOwnerPermissions below, which are the
    // members:* permissions the retention screens use.
    ClubOwner: 'admin:club-owner',
    FrontOfHouse: 'admin:frontofhouse'

};

/**
 * Who can reach the Point of Sale screen (/pos), and therefore who is offered the
 * Point of Sale quick action on a member profile. Keep the route gate in App.tsx
 * and the quick-action gate on this one constant — a button that leads somewhere
 * the user cannot go is just a link to /unauthorized.
 */
export const PosScreenPermissions = [
    AdminPermissions.ClubAdmin,
    AdminPermissions.FortiusAdmin,
    AdminPermissions.FrontOfHouse,
];

export const PaymentPermissions = {
    ReadPayments: 'payments:read',
    CreatePayments: 'payments:create',
    UpdatePayments: 'payments:update',
    DeletePayments: 'payments:delete',
    ReadDirectDebits: 'directdebit:read'
};

/**
 * The Staff tier — reception team accounts. Same access as front-of-house plus the CRM.
 *
 * These exist because the CRM screens used to be gated on `admin:club`, which also makes a
 * user "admin" (see MemberContext.isAdmin) and therefore grants view-all over leads. That
 * made per-owner lead scoping unreachable: anyone who could open the CRM saw everything.
 */
export const StaffPermissions = {
    /** Do your own work + manage CRM leads. What a Staff account gets. */
    PerformTasks: 'staff:tasks:perform',
    /** Allocate work to others, and reach the screens that do it. Manager tier. */
    CreateTasks: 'staff:tasks:create',
    /** Reassign a rota slot, edit the roster, hand a lead to someone else. Manager tier. */
    EditTasks: 'staff:tasks:edit',
    /** Remove an allocation or rota slot. Manager tier. */
    DeleteTasks: 'staff:tasks:delete'
};

/**
 * Promotion definitions. `promotions:read` is held by anyone who may APPLY a promotion —
 * front-of-house and Staff included — not just the club admins who author them. Authoring
 * (the /pos/promotions screen and the mutating endpoints) stays on `admin:club`.
 */
export const PromotionPermissions = {
    // Key is qualified (like PaymentPermissions.ReadPayments) rather than a bare `Read`
    // because AllPermissions below spreads every group into one object: a second `Read`
    // key would overwrite BookingPermissions.Read.
    ReadPromotions: 'promotions:read'
};

/**
 * Who can read the club's promotion definitions, and therefore who is offered the Add
 * Promotion action on a member profile. Mirrors the API's `CanReadPromotions` policy
 * (`promotions:read` OR `members:update` OR `admin:club`, with `admin:fortius` bypassing)
 * — keep the two in step, since a button that opens a modal the API will refuse to
 * populate is the bug FORT-398 fixed.
 *
 * `members:update` is the APPLY gate, named on the API policy so read can never be
 * stricter than apply. It must be named here too: leaving it out would hide the action
 * from reception (who holds it) while the API happily served them — the same
 * frontend-vs-API divergence as the original bug, just pointing the other way.
 */
export const PromotionReadPermissions = [
    PromotionPermissions.ReadPromotions,
    MemberPermissions.UpdateMember,
    AdminPermissions.ClubAdmin,
    AdminPermissions.FortiusAdmin,
];

export const ClubOwnerPermissions = {
    ManageFollowups: 'members:manage-followups',
    ViewAllLeads: 'members:view-all-leads'
}

export const AllPermissions = {
    ...MemberPermissions,
    ...BookingPermissions,
    ...AdminPermissions,
    ...PaymentPermissions,
    ...StaffPermissions,
    ...PromotionPermissions,
    ...ClubOwnerPermissions
}